import type { ReactNode } from "react";
import { Toast, TableSkeleton, Pagination, useToast } from "@hiya/ui";
import { SearchBar } from "./SearchBar";
import type { KitRecord } from "../../types/orders";

interface KitListLayoutProps {
  kit: {
    items: KitRecord[];
    loading: boolean;
    error: string | null;
    search: string;
    setSearch: (v: string) => void;
    handleSearch: () => void;
    handleClearSearch: () => void;
    page: number;
    setPage: (p: number) => void;
    totalPages: number;
  };
  toast: ReturnType<typeof useToast>["toast"];
  columns: readonly string[];
  renderRow: (item: KitRecord) => ReactNode;
  rowClassName?: (item: KitRecord) => string;
  children?: ReactNode;
}

export function KitListLayout({ kit, toast, columns, renderRow, rowClassName, children }: Readonly<KitListLayoutProps>) {
  return (
    <div>
      <Toast message={toast.message} visible={toast.visible} variant={toast.variant} />

      <div className="mb-5">
        <SearchBar
          value={kit.search}
          onChange={kit.setSearch}
          onSearch={kit.handleSearch}
          onClear={kit.handleClearSearch}
        />
      </div>

      {kit.loading && <TableSkeleton columns={columns.length} />}

      {!kit.loading && kit.error && (
        <div className="text-center py-20">
          <p className="text-text-secondary text-base">{kit.error}</p>
        </div>
      )}

      {!kit.loading && !kit.error && kit.items.length === 0 && (
        <div className="text-center py-20">
          <p className="text-text-secondary text-base">No results found</p>
        </div>
      )}

      {!kit.loading && !kit.error && kit.items.length > 0 && (
        <>
          <div className="border border-stroke-primary rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-bg-tertiary border-b border-stroke-primary">
                  {columns.map((col) => (
                    <th key={col} className="p-4 text-sm font-normal text-text-tertiary text-left">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {kit.items.map((item) => (
                  <tr
                    key={item.record_id}
                    className={`border-b border-stroke-primary last:border-b-0 ${rowClassName?.(item) ?? ""}`}
                  >
                    {renderRow(item)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-5">
            <Pagination currentPage={kit.page} totalPages={kit.totalPages} onPageChange={kit.setPage} />
          </div>
        </>
      )}

      {children}
    </div>
  );
}
